/**
 * Hook for fetching analysis data (measurements, dosing, task events) for correlation charts.
 *
 * Uses the batch /api/tanks/{tank_id}/analysis endpoint that returns:
 * - measurements: Grouped by parameter key and source
 * - dosing: Aggregated by day and chemical
 * - events: Task completions (date, task name, task type, notes)
 * - available_parameters, available_dosing, available_events: For sidebar population
 */
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import { CACHE_TIMES } from '@/config/cacheTimes';

// Response types matching backend schema
export interface MeasurementPoint {
  timestamp: string;
  value: number;
  device: string;
  note?: string;
}

export interface ParameterSource {
  key: string;
  source: string;
}

export interface DosingPoint {
  date: string;
  volume_ml: number;
}

export interface TaskEvent {
  date: string;
  datetime?: string;  // Full ISO timestamp for precise chart positioning
  type: string;  // Task definition name
  task_type: string;  // Task type (water_change, testing, etc.)
  notes?: string;
}

export interface AnalysisData {
  measurements: Record<string, MeasurementPoint[]>;
  dosing: Record<string, DosingPoint[]>;
  events: TaskEvent[];  // Changed from MaintenanceEvent[]
  available_parameters: string[];
  available_parameters_by_source: ParameterSource[];
  available_dosing: string[];
  available_events: string[];
  performance?: Record<string, number>;
}

export interface UseAnalysisDataParams {
  tankId: string | undefined;
  startDate: string;  // YYYY-MM-DD format
  endDate: string;    // YYYY-MM-DD format
  /** Optional: Only fetch specific parameters (comma-separated) */
  parameters?: string[];
  /** Include dosing data (default: true) */
  includeDosing?: boolean;
  /** Include events data (default: true) */
  includeEvents?: boolean;
}

/**
 * Fetch analysis data for correlation charts.
 *
 * This hook fetches:
 * - Measurements grouped by parameter key
 * - Dosing aggregated by day and chemical
 * - Maintenance events
 * - Available parameters, dosing, and events for sidebar population
 */
export function useAnalysisData({
  tankId,
  startDate,
  endDate,
  parameters,
  includeDosing = true,
  includeEvents = true,
}: UseAnalysisDataParams): UseQueryResult<AnalysisData> {
  return useQuery({
    queryKey: [
      'analysis',
      tankId,
      startDate,
      endDate,
      parameters?.join(','),
      includeDosing,
      includeEvents,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        include_dosing: String(includeDosing),
        include_events: String(includeEvents),
      });

      if (parameters && parameters.length > 0) {
        searchParams.set('parameters', parameters.join(','));
      }

      const response = await api.get<AnalysisData>(
        `/api/tanks/${tankId}/analysis?${searchParams.toString()}`
      );

      return response;
    },
    enabled: !!tankId && !!startDate && !!endDate,
    staleTime: CACHE_TIMES.DATA, // data only updates from collectors
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Extract available data for sidebar from analysis response.
 * Returns parameter-source composite keys for measurement selection.
 */
export function extractAvailableData(data: AnalysisData | undefined): {
  parameters: string[];
  dosing: string[];
  events: string[];
} {
  if (!data) {
    return { parameters: [], dosing: [], events: [] };
  }

  // Use parameter-source combinations if available
  let parameters: string[];
  if (data.available_parameters_by_source && data.available_parameters_by_source.length > 0) {
    parameters = data.available_parameters_by_source.map(
      (ps) => `${ps.key}:${ps.source}`
    );
  } else {
    // Fallback to simple parameters list (backwards compatibility)
    parameters = data.available_parameters;
  }

  return {
    parameters,
    dosing: data.available_dosing,
    events: data.available_events,
  };
}
